import React, { useState, useEffect, useMemo, useRef } from 'react'
import './ReportLibrary.scss'
import { useDispatch, useSelector } from 'react-redux';
import { MotifTable, MotifProgressLoader, MotifButton, MotifPaginationSelect, MotifPaginationSelectItem } from '@ey-xd/motif-react'
import MotifTabNavigation, { MotifTabControl } from '@ey-xd/motif-react/components/tab-navigation'
import { getReportLibrary, udpateReportLibrary, deleteReport, downloadReport } from '../../redux/actions/ReportLibrary/reportLibrary'
import DeleteModal from './DeleteModal/DeleteModal'
import EditModal from './EditModal/EditModal'
import Search from '../../common/components/Search/Search'
import Toast from '../../common/components/Toast/Toast'
import Actions from './Actions/Actions'
import { useNavigate } from 'react-router-dom';
import { getVulnerabilityCategories, getReportByID, resetReportInfo } from '../../redux/actions/SideBar/sideBar';
import { avoidXSSattack } from 'utils/security';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function ReportLibrary() {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [deleteItemId, setDeleteItemId] = useState(-1)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editItemlId, setEditItemId] = useState(-1)
  const [searchValue, setSearchValue] = useState('')
  const [toastVisible, setToastVisible] = useState(false)
  const [toastData, setToastData] = useState<any>()
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const gridApiRef = useRef<any>(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const reportLibrary = useSelector((state: any) => state?.reportLibrary?.data) || false
  const currentTenantId = useSelector((state: any) => state?.user?.currentTenant?.TenantId);
  const currentTenant = useSelector((state: any) => state?.user?.currentTenant);
  const [filteredData, setFilteredData] = useState<any[]>([])
  const isAiaTenant = (currentTenant?.TenantName || "").toLowerCase() === "aia";
  const isAllianceTenant = (currentTenant?.TenantName || "").toLowerCase() === "allianz";

  const getReportId = (item: any) => item?.Id ?? item?.ReportId;
  const getRowKey = (item: any) => item?.__rowKey ?? `${getReportId(item) ?? 'NO_ID'}`;
  const getVersions = (item: any) => Array.isArray(item?.Versions) ? item?.Versions : [];
  const getVersionCount = (item: any) => getVersions(item).length;
  const getLatestVersionId = (item: any) => {
    const versions = getVersions(item);
    if (!versions.length) {
      return undefined;
    }
    const latestVersion = versions.find((version: any) => version?.IsLatest);
    return latestVersion?.VersionId ?? versions[0]?.VersionId;
  };

  const formatDateTime = (value: any) => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      const matchedDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (matchedDate?.[1]) {
        return matchedDate[1];
      }
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return `${value}`;
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const columnConfig = useMemo(() => ([
    {
      field: 'ReportName',
      headerName: 'Report Name',
      sortable: true,
      width: 400,
      cellRenderer: 'reportNameCellRenderer',
    },
    isAiaTenant ? {
      field: 'WorkOrderNumber',
      headerName: 'Work Order Number',
      sortable: true,
      width: 240,
      cellRenderer: 'workOrderCellRenderer',
    } : null,
    isAiaTenant ? {
      field: 'Versions',
      headerName: 'Versions',
      sortable: true,
      width: 220,
      cellRenderer: 'versionsCellRenderer',
    } : null,
    !isAiaTenant ? {
      field: 'StartDate',
      headerName: 'Start Date',
      cellRenderer: 'dateCellRenderer',
      sortable: true,
      width: 200
    } : null,
    !isAiaTenant ? {
      field: 'EndDate',
      headerName: 'End Date',
      cellRenderer: 'dateCellRenderer',
      sortable: true,
      width: 200
    } : null,
    {
      field: 'actions',
      cellRenderer: 'actionsCellRenderer',
      headerName: 'Actions',
      sortable: false,
      className: 'testActions'
    }
  ].filter(Boolean)), [isAiaTenant]);

  useEffect(() => {
    dispatch(getReportLibrary({ "user": "Admin" }))
    dispatch(resetReportInfo())
  }, [])

  useEffect(() => {
    if (currentTenantId) {
      dispatch(getReportLibrary({ "user": "Admin" }))
    }
  }, [currentTenantId])

  useEffect(() => {
    setFilteredData(reportLibrary?.data || [])
    setExpandedRowKeys([])
    setCurrentPage(1)
  }, [reportLibrary])

  const handleDeleteReport = () => {
    const idToDelete = deleteItemId;
    dispatch(deleteReport(idToDelete)).then(() => {
      setFilteredData((prev: any[]) =>
        prev.filter((item: any) => getReportId(item) !== idToDelete)
      );      dispatch(getReportLibrary({ "user": "Admin" }));      setToastVisible(true)
      setToastData({
        header: "Successfully deleted",
        message: "Your have successfully deleted report. It won’t be available in Report’s Library any more.",
        variant: "success"
      })
    }).catch(() => {
      setToastVisible(true)
      setToastData({
        header: "Failed",
        message: "Failed to delete report. Please try again.",
        variant: "error"
      })
    })
    setDeleteItemId(-1)
  }

  const handleUdpateReport = (item: any) => {
    const newItem = { ...item }
    dispatch(udpateReportLibrary(newItem))
    setDeleteModalVisible(false)
    setEditItemId(-1)
  }

  const handleSearch = (e: any) => {
    e = avoidXSSattack(e);
    const searchText = (e?.target?.value || '').toLowerCase();
    setSearchValue(e.target.value)
    const filtered = reportLibrary?.data?.filter((item: any) => {
      const reportName = (item?.ReportName || '').toLowerCase();
      const workOrderNumber = String(item?.WorkOrderNumber || item?.WorkOrder || '').toLowerCase();
      return reportName.includes(searchText) || workOrderNumber.includes(searchText);
    }) || []
    setFilteredData(filtered)
    setExpandedRowKeys([])
    setCurrentPage(1)
  }

  const toggleRowExpansion = (item: any) => {
    const versions = getVersions(item);
    if (!versions.length) {
      return;
    }

    const selectedKey = getRowKey(item);

    setExpandedRowKeys((previous) =>
      previous.includes(selectedKey)
        ? previous.filter((key) => key !== selectedKey)
        : [...previous, selectedKey]
    );
  };

  const handleRowClick = (params: any) => {
    if (!params?.data || params?.data?._isDetailRow) {
      return;
    }

    const target = params?.event?.target;
    if (
      target?.closest?.('.reportLibraryActions')
      || target?.closest?.('.motif-button')
    ) {
      return;
    }

    toggleRowExpansion(params?.data);
  };

  const downloadReportHandler = async (id: any, itemData?: any) => {
    try {
      const item = itemData || filteredData?.find((d: any) => getReportId(d) === id);
      const latestVersionId = isAiaTenant ? getLatestVersionId(item) : undefined;
      const downloadPayload: any = { report_id: id };
      if (isAiaTenant && latestVersionId) {
        downloadPayload.version_id = latestVersionId;
      }
      const result = await dispatch(downloadReport(downloadPayload));
      const apiFilename = isAiaTenant ? (result?.filename || null) : null;
      const reportName = isAllianceTenant
        ? (item?.ReportName ? item.ReportName + '.docx' : 'report.docx')
        : apiFilename || item?.ReportFileName || (item?.ReportName ? item.ReportName + '.docx' : 'report.docx');

      const file = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = reportName;
      link.click();
      link.remove();
      URL.revokeObjectURL(fileURL);
      setToastVisible(true)
      setToastData({
        header: "Success",
        message: "Report downloaded successfully",
        variant: "success"
      });
    } catch (error) {
      setToastVisible(true);
      setToastData({
        header: "Failed",
        message: "Unable to download Report. Please try again.",
        variant: "error"
      });
    }
  };

  const handleEdit = (id: any, versionId?: any, isLatestVersion: boolean = true) => {
    setEditItemId(id);
    dispatch(getVulnerabilityCategories());
    const payload: any = {
      report_id: id,
      isAiaTenant,
      mode: isLatestVersion ? "edit" : "view",
      isLatestVersion,
      ...(isAiaTenant && !isLatestVersion && versionId ? { version_id: versionId } : {}),
      ...(isAiaTenant && versionId ? { versionId } : {}),
    };
    dispatch(getReportByID(payload)).then(() => {
      const resolvedVersionId = payload?.version_id || payload?.versionId;
      navigate('/reportGenerator', {
        state: {
          mode: payload.mode,
          isLatestVersion: payload.isLatestVersion,
          ...(isAiaTenant && resolvedVersionId ? { versionId: resolvedVersionId } : {}),
        },
      })
    })
  }

  const handleMainRowEdit = (item: any) => {
    const reportId = getReportId(item);
    if (reportId === undefined || reportId === null) {
      return;
    }
    const latestVersionId = isAiaTenant ? getLatestVersionId(item) : undefined;
    handleEdit(reportId, latestVersionId, true);
  }

  const handleVersionAction = (item: any, version: any) => {
    const reportId = getReportId(item);
    if (reportId === undefined || reportId === null) {
      return;
    }
    const versionId = isAiaTenant ? version?.VersionId : undefined;
    const isLatestVersion = isAiaTenant ? !!version?.IsLatest : true;
    handleEdit(reportId, versionId, isLatestVersion);
  }

  const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / pageSize));

  const visibleCount = Math.min(pageSize, Math.max(0, (filteredData?.length || 0) - (currentPage - 1) * pageSize));

  const rowData = useMemo(() => {
    const rows: any[] = [];
    const pageStart = (currentPage - 1) * pageSize;
    const pageEnd = pageStart + pageSize;
    const pageItems = (filteredData || []).slice(pageStart, pageEnd);
    pageItems.forEach((item: any, localIndex: number) => {
      const rowIndex = pageStart + localIndex;
      const reportId = getReportId(item);
      const rowKey = `${reportId ?? 'NO_ID'}-${rowIndex}`;
      const mainRow = {
        ...item,
        __rowKey: rowKey,
      };
      rows.push(mainRow);

      if (!expandedRowKeys.includes(rowKey)) {
        return;
      }

      const versions = getVersions(item);
      versions.forEach((version: any, index: number) => {
        rows.push({
          ...mainRow,
          _isDetailRow: true,
          _parentKey: rowKey,
          _detailIndex: index,
          _version: version,
          ReportName: mainRow?.ReportName || '',
          WorkOrderNumber: mainRow?.WorkOrderNumber || mainRow?.WorkOrder || '',
          StartDate: mainRow?.StartDate,
          EndDate: mainRow?.EndDate,
          __detailKey: `${rowKey}-${version?.VersionId || index}`
        });
      });
    });
    return rows;
  }, [currentPage, pageSize, expandedRowKeys, filteredData]);

  return (
    <div className="reportLibraryWrapper">
      <MotifProgressLoader show={reportLibrary?.loading} />
      <div className="breadCrumbs">Reports</div>
      <div className="heading">
        <div className="title">Report Library</div>
        <div className="description">Welcome to the Report Library, your centralized hub for all generated Penetration Testing Reports. Here you have the option to edit, delete or download your tailored reports at your convenience.</div>
      </div>
      <div className="reportLibrary__content">
        <MotifTabNavigation defaultActiveKey={0}>
          <MotifTabControl>
            My Reports
          </MotifTabControl>
        </MotifTabNavigation>
        <Search
          value={searchValue}
          onChange={handleSearch}
          placeholder={"Search work order, app, or report..."}
        />
        <div className='reportLibrary__tableWrapper'>
          {filteredData?.length > 0 && (
            <MotifTable
              columnDefs={columnConfig}
              components={{
                reportNameCellRenderer: (p: any) => {
                  if (p?.data?._isDetailRow) {
                    return (
                      <div className="reportLibrarySubRowVersion">
                        <span className="reportLibrarySubRowText">{p?.data?._version?.VersionNumber || ''}</span>
                        {!!p?.data?._version?.IsLatest && <span className="reportLibraryLatestFlag">Latest</span>}
                      </div>
                    );
                  }

                  const versions = getVersions(p?.data);
                  const isExpanded = expandedRowKeys.includes(getRowKey(p?.data));
                  return (
                    <div className="reportLibraryMainValue reportLibraryRowTitle">
                      <span>{p?.value || ''}</span>
                    </div>
                  );
                },
                workOrderCellRenderer: (p: any) => (
                  p?.data?._isDetailRow
                    ? <span className="reportLibrarySubRowText">{formatDateTime(p?.data?._version?.VersionDate) || 'NA'}</span>
                    : <div className="reportLibraryMainValue">{p?.value || ''}</div>
                ),
                versionsCellRenderer: (p: any) => (
                  p?.data?._isDetailRow ? (
                    <MotifButton
                      variant={p?.data?._version?.IsLatest ? 'primary' : 'secondary'}
                      onClick={(event: any) => {
                        event.stopPropagation();
                        handleVersionAction(p?.data, p?.data?._version);
                      }}
                    >
                      {p?.data?._version?.IsLatest ? 'Edit' : 'View'}
                    </MotifButton>
                  ) : (
                    <div className="reportLibraryMainValue">
                      <span className="reportLibraryVersionCount">{getVersionCount(p?.data)} Versions</span>
                    </div>
                  )
                ),
                actionsCellRenderer: (p: any) => (
                  p?.data?._isDetailRow ? null : (
                    <div className="reportLibraryMainValue reportLibraryActions">
                      <Actions
                        id={getReportId(p?.data)}
                        setDeleteItemId={setDeleteItemId}
                        setEditItemId={() => handleMainRowEdit(p?.data)}
                        setEditModalVisible={() => {}}
                        setDeleteModalVisible={setDeleteModalVisible}
                        downloadReport={() => downloadReportHandler(getReportId(p?.data), p?.data)}
                      />
                    </div>
                  )
                ),
                dateCellRenderer: (p: any) => (
                  p?.data?._isDetailRow
                    ? null
                    : <div className="reportLibraryMainValue">{p?.value ? new Date(p.value).toLocaleDateString() : ""}</div>
                )
              }}
              onFilterChanged={function noRefCheck(){}}
              onGridReady={(params: any) => {
                gridApiRef.current = params?.api;
              }}
              onSortChanged={function noRefCheck(){}}
              getRowHeight={(params: any) => params?.data?._isDetailRow ? 56 : 62}
              onRowClicked={handleRowClick}
              rowClassRules={{
                reportLibraryMainRow: (params: any) => !params?.data?._isDetailRow,
                reportLibraryDetailRow: (params: any) => !!params?.data?._isDetailRow,
              }}
              rowData={rowData}
              suppressCellFocus
              suppressRowClickSelection
              style={{ height: 'calc(100vh - 400px)', minHeight: '360px' }}
            />
          )}
        </div>
        {filteredData?.length > 0 && (
          <div className="reportLibraryCustomPagination">
            <span className="reportLibraryPaginationCount">{visibleCount} of {filteredData.length}</span>
            <div className="reportLibraryPaginationNavWrapper">
              <MotifPaginationSelect>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MotifPaginationSelectItem
                    key={size}
                    selected={pageSize === size}
                    onClick={() => { setPageSize(size); setCurrentPage(1); }}
                  >
                    Show {size}
                  </MotifPaginationSelectItem>
                ))}
              </MotifPaginationSelect>
              <div className="reportLibraryPaginationNav">
                <button className="reportLibraryNavBtn" onClick={() => setCurrentPage(1)} disabled={currentPage <= 1}>{'|<'}</button>
                <button className="reportLibraryNavBtn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>{'<'}</button>
                <span className="reportLibraryPageIndicator">{currentPage}/{totalPages}</span>
                <button className="reportLibraryNavBtn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>{'>'}</button>
                <button className="reportLibraryNavBtn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>{'>|'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <DeleteModal
        report={filteredData?.length > 0 && filteredData?.find((data: any) => getReportId(data) === deleteItemId) || {}}
        isVisible={deleteModalVisible}
        setIsVisible={setDeleteModalVisible}
        onConfirmClick={handleDeleteReport}
      />
      <EditModal
        report={filteredData?.length > 0 && filteredData?.find((data: any) => getReportId(data) === editItemlId) || {}}
        isVisible={editModalVisible}
        setIsVisible={setEditModalVisible}
        onConfirmClick={handleUdpateReport}
      />
      <Toast
        visible={toastVisible}
        setVisible={setToastVisible}
        children={
          <div>
            <div className={'toastHeader'}>{toastData?.header}</div>
            <div className={'toastContent'}>{toastData?.message}</div>
          </div>
        }
        variant={toastData?.variant}
      />
    </div>
  )
}

export default ReportLibrary
